import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import StatusIndicatorBar from '../../components/ui/StatusIndicatorBar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import ProductCard from './components/ProductCard';
import ProductEditorModal from './components/ProductEditorModal';
import CSVImportModal from './components/CSVImportModal';
import BulkActionsModal from './components/BulkActionsModal';
import { productService } from '../../services/productService';
import { triggerAutoReload } from '../../utils/autoReload';

const EcommerceHub = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [marketplaceFilter, setMarketplaceFilter] = useState('');
  
  // Selection and modals
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [products, searchQuery, categoryFilter, brandFilter, marketplaceFilter]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const allProducts = await productService?.getByType('product');
      setProducts(allProducts || []);
      setError('');
    } catch (err) {
      console.error('Error loading products:', err);
      setError(err?.message || 'Failed to load products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...products];

    // Search by name or SKU
    if (searchQuery) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(p => 
        p?.name?.toLowerCase()?.includes(query) ||
        p?.sku?.toLowerCase()?.includes(query)
      );
    }

    // Category filter
    if (categoryFilter) {
      filtered = filtered?.filter(p => p?.category === categoryFilter);
    }

    // Brand filter
    if (brandFilter) {
      filtered = filtered?.filter(p => p?.brand === brandFilter);
    }

    // Marketplace filter
    if (marketplaceFilter) {
      filtered = filtered?.filter(p => {
        const metadata = p?.metadata || {};
        return metadata?.[`${marketplaceFilter}Active`] === true;
      });
    }

    setFilteredProducts(filtered);
  };

  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev => {
      if (prev?.includes(productId)) {
        return prev?.filter(id => id !== productId);
      }
      return [...prev, productId];
    });
  };

  // Pagination
  const totalPages = Math.ceil(filteredProducts?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = filteredProducts?.slice(startIndex, startIndex + itemsPerPage);

  const handleSelectAll = () => {
    if (selectedProducts?.length === paginatedProducts?.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(paginatedProducts?.map(p => p?.id));
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
  };

  const handleSaveProduct = async (updatedProduct) => {
    try {
      if (updatedProduct?.id) {
        await productService?.update(updatedProduct?.id, updatedProduct);
      } else {
        await productService?.create(updatedProduct);
      }
      await loadProducts();
      setEditingProduct(null);
      
      // Trigger auto-reload
      triggerAutoReload('product-saved');
      
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'success',
          message: updatedProduct?.id ? 'Produto atualizado com sucesso' : 'Produto criado com sucesso',
          progress: 100
        }
      }));
    } catch (err) {
      console.error('Error saving product:', err);
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'error',
          message: 'Erro ao salvar produto',
          progress: 0
        }
      }));
    }
  };

  const handleCSVImport = async (importedProducts) => {
    try {
      await loadProducts();
      setShowCSVModal(false);
      
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'success',
          message: `${importedProducts?.length} produtos importados com sucesso`,
          progress: 100
        }
      }));
    } catch (err) {
      console.error('Error importing CSV:', err);
    }
  };

  const handleBulkAction = async (action, value) => {
    try {
      const selectedProductsData = products?.filter(p => selectedProducts?.includes(p?.id));
      
      for (const product of selectedProductsData) {
        const updates = {};
        
        if (action === 'price') {
          updates.price = parseFloat(value);
        } else if (action === 'publish') {
          updates.metadata = {
            ...product?.metadata,
            [`${value}Active`]: true
          };
        } else if (action === 'delete') {
          await productService?.delete(product?.id);
          continue;
        }
        
        await productService?.update(product?.id, updates);
      }

      await loadProducts();
      
      // Trigger auto-reload
      triggerAutoReload('bulk-action');
      
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'success',
          message: `${selectedProducts?.length} produtos atualizados com sucesso`,
          progress: 100
        }
      }));
    } catch (err) {
      console.error('Error applying bulk action:', err);
    }
  };

  const categoryOptions = [
    { value: '', label: 'Todas as Categorias' },
    { value: 'Brinquedos', label: 'Brinquedos' },
    { value: 'Utilidades Domésticas', label: 'Utilidades Domésticas' },
    { value: 'Escolar', label: 'Escolar' },
    { value: 'eletronicos', label: 'Eletrônicos' },
    { value: 'moda', label: 'Moda' }
  ];

  const brandOptions = [
    { value: '', label: 'Todas as Marcas' },
    { value: 'DDG', label: 'DDG' },
    { value: 'generico', label: 'Genérico' },
    { value: 'samsung', label: 'Samsung' }
  ];

  const marketplaceOptions = [
    { value: '', label: 'Todos os Marketplaces' },
    { value: 'mercadolivre', label: 'Mercado Livre' },
    { value: 'shopee', label: 'Shopee' },
    { value: 'amazon', label: 'Amazon' },
    { value: 'magalu', label: 'Magazine Luiza' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <StatusIndicatorBar />
      
      <div className="container mx-auto px-4 py-6 lg:px-8">
        <BreadcrumbNavigation
          items={[
            { label: 'Dashboard', path: '/' },
            { label: 'VERTEX COMMERCE', path: '/ecommerce-hub' }
          ]}
        />

        {/* Header Section */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">VERTEX COMMERCE</h1>
            <p className="text-muted-foreground">Gerenciamento completo de produtos para marketplaces</p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              iconName="Upload"
              iconPosition="left"
              onClick={() => setShowCSVModal(true)}
            >
              Importar CSV
            </Button>
            
            <Button
              iconName="Plus"
              iconPosition="left"
              onClick={() => setEditingProduct({})}
            >
              Novo Produto
            </Button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Input
              placeholder="Buscar por nome ou SKU..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
            />
            
            <Select
              options={categoryOptions}
              value={categoryFilter}
              onChange={setCategoryFilter}
              placeholder="Categoria"
            />
            
            <Select
              options={brandOptions}
              value={brandFilter}
              onChange={setBrandFilter}
              placeholder="Marca"
            />
            
            <Select
              options={marketplaceOptions}
              value={marketplaceFilter}
              onChange={setMarketplaceFilter}
              placeholder="Marketplace"
            />
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedProducts?.length > 0 && (
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon name="CheckSquare" size={20} color="var(--color-primary)" />
              <span className="font-medium text-foreground">
                {selectedProducts?.length} produto(s) selecionado(s)
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="Edit"
                onClick={() => setShowBulkModal(true)}
              >
                Editar em Massa
              </Button>
              
              <Button
                variant="destructive"
                size="sm"
                iconName="Trash2"
                onClick={() => handleBulkAction('delete')}
              >
                Excluir
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                iconName="X"
                onClick={() => setSelectedProducts([])}
              >
                Cancelar
              </Button>
            </div>
          </div>
        )}

        {/* Results Count */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-muted-foreground">
            {filteredProducts?.length} produto(s) encontrado(s)
          </p>
          
          <Button
            variant="ghost"
            size="sm"
            iconName={selectedProducts?.length === paginatedProducts?.length ? 'CheckSquare' : 'Square'}
            onClick={handleSelectAll}
          >
            Selecionar Todos
          </Button>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Icon name="Loader2" size={40} className="animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 text-center">
            <Icon name="AlertCircle" size={40} className="mx-auto mb-3 text-destructive" />
            <p className="text-destructive">{error}</p>
          </div>
        ) : paginatedProducts?.length === 0 ? (
          <div className="bg-muted/50 border border-border rounded-lg p-12 text-center">
            <Icon name="Package" size={60} className="mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Nenhum produto encontrado</h3>
            <p className="text-muted-foreground mb-4">Comece importando produtos via CSV ou criando manualmente</p>
            <Button
              iconName="Plus"
              onClick={() => setEditingProduct({})}
            >
              Criar Primeiro Produto
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {paginatedProducts?.map(product => (
                <ProductCard
                  key={product?.id}
                  product={product}
                  isSelected={selectedProducts?.includes(product?.id)}
                  onSelect={() => handleSelectProduct(product?.id)}
                  onEdit={() => handleEditProduct(product)}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ChevronLeft"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => prev - 1)}
                />
                
                {Array.from({ length: totalPages }, (_, i) => i + 1)?.map(page => (
                  <Button
                    key={page}
                    variant={currentPage === page ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Button>
                ))}
                
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ChevronRight"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(prev => prev + 1)}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {editingProduct && (
        <ProductEditorModal
          isOpen={true}
          product={editingProduct}
          onClose={() => setEditingProduct(null)}
          onSave={handleSaveProduct}
        />
      )}

      {showCSVModal && (
        <CSVImportModal
          onClose={() => setShowCSVModal(false)}
          onImport={handleCSVImport}
        />
      )}

      {showBulkModal && (
        <BulkActionsModal
          selectedCount={selectedProducts?.length}
          onClose={() => setShowBulkModal(false)}
          onApply={handleBulkAction}
        />
      )}
    </div>
  );
};

export default EcommerceHub;